import styled from '@emotion/styled';
import { IMatch } from 'domain/entities/Match/Match';
import NoImage from 'ui/images/no_image_case.png';
import { mqUser } from 'utils/responsive';
import { UserIcon } from 'ui/components/elements/UserIcon/UserIcon';
import LinkIcon from 'ui/images/link.svg';
import dayjs from 'dayjs';
import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';
import { AGENT_TYPES } from 'domain/entities/Agent/Agent';

const Wrapper = styled.div<{ isPremium: boolean }>`
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    background: ${(props) => (props.isPremium ? 'linear-gradient(to right, #fff8e1, #ffeb99)' : 'var(--white)')};
    padding: 20px;
    margin: 0 auto;
    position: relative;

    ${mqUser('SP')} {
        padding: 16px 12px;
    }
`;

const ImageWrapper = styled.div<{ noImg: boolean }>`
    width: 140px;
    height: 140px;
    border-radius: 10px;
    background-color: #808080;
    position: relative;
    overflow: hidden;
    cursor: pointer;

    :hover {
        opacity: 0.8;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    ${mqUser('SP')} {
        width: 100%;
        height: auto;
        max-height: 200px;
        aspect-ratio: 4 / 3;
    }
`;

const Flex = styled.div`
    display: flex;
    gap: 20px;

    ${mqUser('SP')} {
        flex-direction: column;
        gap: 12px;
    }
`;

const FlexCenter = styled.div`
    display: flex;
    gap: 13px;
    align-items: center;
`;

const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Title = styled.div`
    font-size: var(--font-x-large);
    font-weight: var(--font-bold);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 1px;
    :hover {
        opacity: 0.8;
    }

    img {
        width: 16px;
        height: 16px;
    }
`;

const Label = styled.div`
    color: var(--white);
    background: linear-gradient(45deg, #daa520, #b8860b);
    padding: 0.2rem 0.9rem;
    border-radius: 4px;
    font-size: var(--font-medium);
    font-weight: 600;
    width: fit-content;
    height: fit-content;
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 20px;
    justify-content: flex-end;
    position: absolute;
    right: 50px;
    bottom: 20px;

    ${mqUser('SP')} {
        position: static;
        justify-content: flex-start;
        flex-direction: column;
        gap: 12px;
        width: 100%;
        margin-top: 20px;

        button:nth-of-type(1) {
            order: 2;
        }
        button:nth-of-type(2) {
            order: 1;
        }
    }
`;

const Button = styled.button`
    background-color: var(--main-color-blue);
    color: var(--white);
    border: none;
    font-weight: 700;
    font-size: var(--font-large);
    border-radius: 5px;
    padding: 8px 20px;
    cursor: pointer;

    :hover {
        opacity: 0.8;
    }

    ${mqUser('SP')} {
        width: 100%;
        font-size: var(--font-medium);
    }
`;

const AcceptButton = styled(Button)`
    background-color: var(--primary);
`;

const DeclineButton = styled(Button)`
    background-color: #696969;
`;

const MessageContainer = styled.div<{ isPremium: boolean }>`
    position: relative;
    width: 100%;
    background: ${(props) => (props.isPremium ? 'var(--white)' : '#f0f0f0')};
    border-radius: 10px;
    text-align: left;
    white-space: pre-line;
    padding: 20px;
    font-size: 14px;
    white-space: pre-wrap;
    word-wrap: break-word;

    &::before {
        content: '';
        position: absolute;
        top: 20px;
        left: -20px;
        margin-top: -10px;
        border: 10px solid transparent;
        border-right: 10px solid ${(props) => (props.isPremium ? 'var(--white)' : '#f0f0f0')};
    }

    ${mqUser('SP')} {
        &::before {
            display: none;
        }
    }
`;

const AgentNameContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    font-weight: var(--font-bold);
    font-size: var(--font-x-large);

    ${mqUser('SP')} {
        font-size: var(--font-medium);
        flex-direction: column;
        align-items: flex-start;
    }
`;

const ElapsedDays = styled.div`
    position: absolute;
    top: 5px;
    left: 5px;
    z-index: 1;
    font-size: var(--font-default);
    color: var(--white);
    background-color: red;
    border-radius: 10px;
    width: fit-content;
    padding: 3px 10px;
    margin-bottom: 5px;
    font-weight: 700;
`;

const LabelsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

interface OfferCardProps {
    offer: IMatch;
    onAccept: (matchId: number) => void;
    onDecline: (matchId: number) => void;
}

export const OfferCard: React.FC<OfferCardProps> = ({ offer, onAccept, onDecline }) => {
    const elapsedDays = dayjs().diff(dayjs(offer.created_at), 'day');
    return (
        <Wrapper isPremium={offer.premium_offer}>
            <Flex>
                <ImageWrapper
                    noImg={!offer.agent.icon_image}
                    onClick={() => {
                        window.open(`/buyer/agent_profile/${offer.agent.id}/`, '_blank');
                    }}
                >
                    <ElapsedDays>
                        {elapsedDays === 0 ? (
                            'NEW'
                        ) : (
                            <>
                                オファーから
                                <br />
                                {elapsedDays}日経過
                            </>
                        )}
                    </ElapsedDays>
                    <img src={offer.agent.icon_image || NoImage} alt={offer.agent.name} />
                </ImageWrapper>
                <FlexColumn>
                    <FlexCenter>
                        <Title
                            onClick={() => {
                                window.open(`/buyer/agent_profile/${offer.agent.id}/`, '_blank');
                            }}
                        >
                            {offer.agent.name}
                            <img src={LinkIcon} alt={'リンクアイコン'} />
                        </Title>
                        <LabelsContainer>
                            {offer.agent.agent_type === AGENT_TYPES.AGENCY.value && (
                                <ColorLabel color="#ee7800" backgroundColor="#fafad2" borderColor="#ee7800">
                                    {AGENT_TYPES.AGENCY.label}
                                </ColorLabel>
                            )}
                            {offer.agent.agent_type === AGENT_TYPES.FA.value && (
                                <ColorLabel color="#006eb0" backgroundColor="#e5ffff" borderColor="#006eb0">
                                    {AGENT_TYPES.FA.label}
                                </ColorLabel>
                            )}
                            {offer.agent.agent_type === AGENT_TYPES.BUSINESS_ACQUISITION.value && (
                                <ColorLabel color="#56256e" backgroundColor="#efefff" borderColor="#56256e">
                                    {AGENT_TYPES.BUSINESS_ACQUISITION.label}
                                </ColorLabel>
                            )}
                            {offer.agent.is_sole_proprietor && (
                                <ColorLabel color="#008000" backgroundColor="#d3ffa8" borderColor="#008000">
                                    個人事業主
                                </ColorLabel>
                            )}
                            {offer.premium_offer && <Label color={'#b8860b'}>プレミアムオファー</Label>}
                        </LabelsContainer>
                    </FlexCenter>
                    <AgentNameContainer>
                        <UserIcon size={30} src={offer.from_agent_user.icon_image} />
                        <span>{`${offer.from_agent_user.last_name} ${offer.from_agent_user.first_name} 様`}</span>
                    </AgentNameContainer>
                    <MessageContainer isPremium={offer.premium_offer}>{offer.message_from_brokerage}</MessageContainer>
                </FlexColumn>
                <ButtonContainer>
                    <DeclineButton onClick={() => onDecline(offer.id)}>お断り</DeclineButton>
                    <AcceptButton onClick={() => onAccept(offer.id)}>承認する</AcceptButton>
                </ButtonContainer>
            </Flex>
        </Wrapper>
    );
};
